// src/pages/SpecialistPanel/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../../layouts/AppLayout';
import apiClient from '../../lib/apiClient';
import WalletWidget from '../../components/wallet/WalletWidget';
import {
  BriefcaseIcon, CheckIcon, ClockIcon, StarIcon,
  CheckCircleIcon, BoltIcon, CalendarDaysIcon, BanknotesIcon,
} from '@heroicons/react/24/outline';
import './styles.css';

const STATUS_LABEL = {
  pending:   { label: 'Aguardando',  cls: 'sp-badge--pending'   },
  accepted:  { label: 'Em andamento',cls: 'sp-badge--accepted'  },
  completed: { label: 'Entregue',    cls: 'sp-badge--completed' },
  approved:  { label: 'Aprovado',    cls: 'sp-badge--approved'  },
  cancelled: { label: 'Cancelado',   cls: 'sp-badge--cancelled' },
};

const TYPE_LABEL = { refinement:'Refino', ads_setup:'Ads Setup', ai_creation:'Criação IA' };
const COST_MAP   = { refinement: 3, ads_setup: 10, ai_creation: 1 };

function SlaCountdown({ deadline }) {
  const [left, setLeft] = useState('');
  useEffect(() => {
    const tick = () => {
      const diff = deadline - Math.floor(Date.now() / 1000);
      if (diff <= 0) { setLeft('⏰ Expirado'); return; }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setLeft(`${h > 0 ? h + 'h ' : ''}${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [deadline]);
  return <span className="sp-sla">{left}</span>;
}

export default function SpecialistPanelPage() {
  const [tab, setTab]           = useState('pool');
  const [profile, setProfile]   = useState(null);
  const [pool, setPool]         = useState([]);
  const [myJobs, setMyJobs]     = useState([]);
  const [applyForm, setApplyForm] = useState({ bio:'', niche:'restaurantes' });
  const [delivery, setDelivery] = useState({});
  const [loading, setLoading]   = useState(false);
  const [toast, setToast]       = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const loadProfile = useCallback(async () => {
    try {
      const r = await apiClient.request('/specialist/profile', { method: 'GET' });
      const d = await r.json();
      if (d.success) setProfile(d.specialist);
    } catch {}
  }, []);

  const loadPool = useCallback(async () => {
    try {
      const r = await apiClient.request('/specialist/pool', { method: 'GET' });
      const d = await r.json();
      if (d.success) setPool(d.pool);
    } catch {}
  }, []);

  const loadMyJobs = useCallback(async () => {
    try {
      const r = await apiClient.request('/job/list?role=specialist', { method: 'GET' });
      const d = await r.json();
      if (d.success) setMyJobs(d.jobs);
    } catch {}
  }, []);

  useEffect(() => {
    loadProfile();
    loadPool();
    loadMyJobs();
  }, [loadProfile, loadPool, loadMyJobs]);

  const handleApply = async () => {
    setLoading(true);
    try {
      const r = await apiClient.request('/specialist/apply', {
        method: 'POST',
        body: JSON.stringify(applyForm),
      });
      const d = await r.json();
      showToast(d.message || 'Candidatura enviada!');
      loadProfile();
    } catch { showToast('Erro ao enviar candidatura'); }
    finally { setLoading(false); }
  };

  const handleAccept = async (jobId) => {
    try {
      const r = await apiClient.request('/job/accept', {
        method: 'POST',
        body: JSON.stringify({ job_id: jobId }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`Job aceito! Entregue antes do prazo.`);
        loadPool(); loadMyJobs();
      } else { showToast(d.message); }
    } catch { showToast('Erro ao aceitar job'); }
  };

  const handleComplete = async (jobId) => {
    const text = delivery[jobId];
    if (!text?.trim()) { showToast('Digite a entrega antes de enviar'); return; }
    try {
      const r = await apiClient.request('/job/complete', {
        method: 'POST',
        body: JSON.stringify({ job_id: jobId, delivery: text }),
      });
      const d = await r.json();
      if (d.success) {
        showToast('Entrega enviada! Aguardando aprovação do cliente.');
        loadMyJobs();
      } else { showToast(d.message); }
    } catch { showToast('Erro ao enviar entrega'); }
  };

  return (
    <AppLayout title="Painel do Especialista" subtitle="Gerencie seus jobs e ganhos" fullWidth>
      {toast && <div className="sp-toast">{toast}</div>}

      {/* Header com status de certificação */}
      <div className="sp-header">
        <div className="sp-header__left">
          <div className="sp-header__avatar"><BriefcaseIcon style={{ width: 28, height: 28 }} /></div>
          <div>
            <div className="sp-header__name">
              {profile ? 'Especialista Ativo' : 'Não cadastrado'}
              {profile?.certified && <span className="sp-cert-badge"><CheckIcon style={{ width: 12, height: 12, display: 'inline', marginRight: 3 }} />Certificado</span>}
              {profile && !profile.certified && <span className="sp-cert-badge sp-cert-badge--pending"><ClockIcon style={{ width: 12, height: 12, display: 'inline', marginRight: 3 }} />Certificação pendente</span>}
            </div>
            {profile && (
              <div className="sp-header__stats">
                <span><StarIcon style={{ width: 13, height: 13, display: 'inline', marginRight: 2 }} />{profile.rating}</span>
                <span><CheckCircleIcon style={{ width: 13, height: 13, display: 'inline', marginRight: 2 }} />{profile.jobs_completed} jobs</span>
                <span>{profile.niche}</span>
                {profile.sla_failures > 0 && <span className="sp-sla-fail">{profile.sla_failures} falhas SLA</span>}
              </div>
            )}
          </div>
        </div>
        <WalletWidget compact={true} />
      </div>

      {/* Tabs */}
      <div className="sp-tabs">
        {[['pool','Pool de Jobs'],['myjobs','Meus Jobs'],['wallet','Carteira'],['apply','Cadastro']].map(([k,l]) => (
          <button key={k} className={`sp-tab${tab===k?' sp-tab--active':''}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {/* ── Pool de Jobs Disponíveis ── */}
      {tab === 'pool' && (
        <div className="sp-section">
          <div className="sp-section__hdr">
            <h3>Jobs disponíveis</h3>
            <button className="sp-refresh" onClick={loadPool}>↻ Atualizar</button>
          </div>
          {!profile?.certified ? (
            <div className="sp-info-box sp-info-box--warn">
              Você precisa ser certificado para ver e aceitar jobs. Vá em <strong>Cadastro</strong> para se candidatar.
            </div>
          ) : pool.length === 0 ? (
            <div className="sp-empty">Nenhum job disponível no momento para o nicho <strong>{profile?.niche}</strong>. Verifique em breve.</div>
          ) : pool.map(job => (
            <div key={job.job_id} className="sp-job-card">
              <div className="sp-job-card__top">
                <span className="sp-job-type">{TYPE_LABEL[job.type] || job.type}</span>
                <span className={`sp-badge ${STATUS_LABEL[job.status]?.cls}`}>{STATUS_LABEL[job.status]?.label}</span>
              </div>
              <div className="sp-job-card__body">
                <div className="sp-job-meta">
                  <span><BoltIcon style={{ width: 13, height: 13, display: 'inline', marginRight: 2 }} />{COST_MAP[job.type]} créditos</span>
                  <span>{job.niche}</span>
                  <span><CalendarDaysIcon style={{ width: 13, height: 13, display: 'inline', marginRight: 2 }} />Aceitar em: <SlaCountdown deadline={job.deadline} /></span>
                </div>
                {job.campaign_id && <div className="sp-job-meta">Campanha: {job.campaign_id}</div>}
              </div>
              <button className="sp-btn sp-btn--accept" onClick={() => handleAccept(job.job_id)}>
                Aceitar Job
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Meus Jobs ── */}
      {tab === 'myjobs' && (
        <div className="sp-section">
          <div className="sp-section__hdr">
            <h3>Meus jobs</h3>
            <button className="sp-refresh" onClick={loadMyJobs}>↻ Atualizar</button>
          </div>
          {myJobs.length === 0 ? (
            <div className="sp-empty">Você ainda não aceitou nenhum job.</div>
          ) : myJobs.map(job => (
            <div key={job.job_id} className={`sp-job-card sp-job-card--${job.status}`}>
              <div className="sp-job-card__top">
                <span className="sp-job-type">{TYPE_LABEL[job.type] || job.type}</span>
                <span className={`sp-badge ${STATUS_LABEL[job.status]?.cls}`}>{STATUS_LABEL[job.status]?.label}</span>
              </div>
              <div className="sp-job-card__body">
                <div className="sp-job-meta">
                  <span><BoltIcon style={{ width: 13, height: 13, display: 'inline', marginRight: 2 }} />{COST_MAP[job.type]} créditos</span>
                  {job.delivery_deadline && <span>Prazo: <SlaCountdown deadline={job.delivery_deadline} /></span>}
                </div>
                {job.delivery && (
                  <div className="sp-delivery-box">
                    <strong>Entrega enviada:</strong>
                    <p>{job.delivery}</p>
                  </div>
                )}
              </div>

              {job.status === 'accepted' && (
                <div className="sp-deliver-form">
                  <textarea
                    className="sp-textarea"
                    placeholder="Descreva a entrega, cole o link ou texto do anúncio…"
                    value={delivery[job.job_id] || ''}
                    onChange={e => setDelivery(prev => ({ ...prev, [job.job_id]: e.target.value }))}
                    rows={4}
                  />
                  <button className="sp-btn sp-btn--complete"
                    onClick={() => handleComplete(job.job_id)}>
                    Enviar entrega
                  </button>
                </div>
              )}

              {job.status === 'approved' && (
                <div className="sp-approved-info">
                  Aprovado! Créditos já liberados na sua carteira.
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Carteira ── */}
      {tab === 'wallet' && (
        <div className="sp-section">
          <WalletWidget />
        </div>
      )}

      {/* ── Cadastro ── */}
      {tab === 'apply' && (
        <div className="sp-section">
          {profile ? (
            <div className="sp-info-box sp-info-box--success">
              Você já está cadastrado como especialista.{' '}
              {profile.certified
                ? 'Certificado e ativo!'
                : 'Aguardando certificação pelo admin da plataforma.'}
            </div>
          ) : null}

          <div className="sp-form">
            <h3>Cadastro de Especialista</h3>
            <p className="sp-form__sub">Preencha seu perfil. A certificação é feita pela equipe MP.</p>

            <label className="sp-label">Nicho de atuação</label>
            <select className="sp-select"
              value={applyForm.niche}
              onChange={e => setApplyForm(p => ({ ...p, niche: e.target.value }))}>
              <option value="restaurantes">Restaurantes</option>
              <option value="varejo">Varejo</option>
              <option value="saude">Saúde & Bem-estar</option>
              <option value="educacao">Educação</option>
              <option value="imoveis">Imóveis</option>
              <option value="geral">Geral</option>
            </select>

            <label className="sp-label">Bio profissional</label>
            <textarea className="sp-textarea"
              rows={4}
              placeholder="Descreva sua experiência com tráfego pago, copywriting, etc."
              value={applyForm.bio}
              onChange={e => setApplyForm(p => ({ ...p, bio: e.target.value }))}
            />

            <button className="sp-btn sp-btn--apply"
              onClick={handleApply} disabled={loading}>
              {loading ? 'Enviando…' : 'Enviar candidatura'}
            </button>
          </div>

          <div className="sp-commission-info">
            <h4><BanknotesIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 4 }} />Comissão da plataforma</h4>
            <div className="sp-commission-row">
              <span>Você recebe</span><span className="sp-commission-pct sp-commission-pct--spec">75%</span>
            </div>
            <div className="sp-commission-row">
              <span>Plataforma</span><span className="sp-commission-pct sp-commission-pct--plat">25%</span>
            </div>
            <p className="sp-commission-note">Créditos liberados via escrow apenas após aprovação do cliente ou expiração do prazo.</p>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
